import TwoFactorSection from '@/components/security/TwoFactorSection';
import React, { useState, useEffect, useMemo } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { format, formatDate } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Switch } from '@/components/ui/switch';
import { useQuery } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { User, CreditCard, Bell, Shield, Clock, LogOut, School, Pencil, Settings, CheckCircle, AppleIcon, Key, Loader2, Globe, HandCoins, Mail, UserCircle, Gift, Link2, Building2, Building, Trophy, Calendar, Users, Award, Star, AlertTriangle, MapPin, GraduationCap, X, Share, Send, Info, Dumbbell, Timer, Zap, RotateCcw, Crown, Tag, Files, HelpCircle, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PaymentElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { BASE_URL, STRIPE_PUBLISHABLE_KEY } from '@/config/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from '@/components/ui/select';
import { useIsMobile } from '@/hooks/use-mobile';
import LeaveSchoolModal from '@/components/settings/LeaveSchoolModal';
import InvitationJoinStudentManager from '@/components/settings/InvitationJoinStudentManager';
import { Features } from '@/components/Features';
import PromoCodeModal from '@/components/PromoCodeModal';

import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

import { AvatarModal } from '@/components/AvatarModal';
import InvitationManager from '@/components/settings/InvitationManager';
import InvitationStudentManager from '@/components/settings/InvitationStudentManager';
import ReferralManager from '@/components/settings/referralManager';
import DeleteAccountModal from '@/components/settings/DeleteAccountModal';



const MyAccount = () => {
  const { toast } = useToast();
  const { user, token, refreshUserData } = useAuth();
  const { logout, logoutAll, logoutToken } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const isMobile = useIsMobile();

  // Help tab visible only when wizard isn't disabled AND onboarding incomplete:
  // hasCreatedEvent must be false, AND at least one of (category/student, plus teacher for schools) is false.
  const showHelpTab = !!user && !user.hasCreatedEvent && (
    !user.hasCreatedCategory ||
    !user.hasCreatedStudent ||
    (user.account_type === 'S' && !user.hasCreatedTeacher)
  );

  const restartWizard = async () => {
    localStorage.removeItem('wizard-completed');
    localStorage.removeItem('wizard-welcome-dismissed');
    localStorage.setItem('wizard-step', '0');
    localStorage.setItem('wizard-active', 'true');
    localStorage.setItem('wizard-started', 'true');
    window.dispatchEvent(new Event('wizard-state-changed'));

    if (token) {
      try {
        await fetch(`${BASE_URL}/api/wizard_enable`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });
        await refreshUserData?.();
      } catch (e) {
        console.error('Failed to disable wizard on server', e);
      }
    }

    navigate('/agenda');
  };

  const [clientSecret, setClientSecret] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [settings, setSettings] = useState({
    email_new_bookings: user.notificationSettings?.email_new_bookings || false,
    email_lesson_reminders: user.notificationSettings?.email_lesson_reminders || false,
    email_schedule_changes: user.notificationSettings?.email_schedule_changes || false,
    email_marketing_updates: user.notificationSettings?.email_marketing_updates || false,
    email_student_off: user.notificationSettings?.email_student_off || false,
    email_teacher_off: user.notificationSettings?.email_teacher_off || false,
    push_new_bookings: user.notificationSettings?.push_new_bookings || false,
    push_lesson_reminders: user.notificationSettings?.push_lesson_reminders || false,
    push_schedule_changes: user.notificationSettings?.push_schedule_changes || false,
    push_student_off: user.notificationSettings?.push_student_off || false,
    push_teacher_off: user.notificationSettings?.push_teacher_off || false,
  });

   const handleToggle = (key: string) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }
  
  const [profile, setProfile] = useState({
    firstname: user?.firstname || '',
    lastname: user?.lastname || '',
    fullname: (user?.firstname || '') + ' ' + (user?.lastname || ''),
    email: user?.email || '',
    account_type: user?.account_type || 'C',
    school_name: user?.school_name || '',
    is_active: user?.is_active || 0,
    avatarUrl: user?.image_url || '',
    phone: user?.phone || '',
    mobile: user?.mobile || '',
    street: user?.street || '',
    street_number: user?.street_number || '',
    zip_code: user?.zip_code || '',
    place: user?.place || '',
    parent_name_1: user?.parent_name_1 || '',
    parent_name_2: user?.parent_name_2 || '',
    mother_email: user?.mother_email || '',
    father_email: user?.father_email || '',
    mother_phone: user?.mother_phone || '',
    father_phone: user?.father_phone || '',
  });

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#8B5CF6',
        colorBackground: '#ffffff',
        colorText: '#1A1F2C',
      },
    },
    loader: 'auto',
    paymentMethodCreation: 'manual',
  };

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const { t, locale } = useLanguage();
  // t(...) can return a string or an array depending on translation content, cast via unknown first to satisfy TypeScript
  const list = (t('notifications.list', { returnObjects: true }) as unknown) as any[];
  const pushList = (t('notifications.pushList', { returnObjects: true }) as unknown) as any[];

  // Filtrage selon le type de compte
const excludedListIds = (profile.account_type === 'S' || profile.account_type === 'C') 
    ? ['email_teacher_off', 'email_new_bookings', 'email_lesson_reminders', 'email_schedule_changes'] // ajoute d'autres IDs à exclure pour S/C
    : ['email_student_off', 'email_marketing_updates']; // IDs à exclure pour les autres comptes

const filteredList = list.filter(item => !excludedListIds.includes(item.id));

const excludedPushIds = (profile.account_type === 'S' || profile.account_type === 'C') 
    ? ['push_teacher_off', 'push_new_bookings', 'push_lesson_reminders', 'push_schedule_changes'] // ajoute d'autres IDs pour S/C
    : ['push_student_off']; // IDs pour les autres

const filteredPushList = pushList.filter(item => !excludedPushIds.includes(item.id));


  const [isCancelPlanDialogOpen, setIsCancelPlanDialogOpen] = useState(false);
  const [isKeepCancelPlanDialogOpen, setKeepIsCancelPlanDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isSavingNotification, setIsSavingNotification] = useState(false);
  const [isChangeCardDialogOpen, setIsChangeCardDialogOpen] = useState(false);
  const [isAddCouponDialogOpen, setIsAddCouponDialogOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [showLeaveSchoolModal, setShowLeaveSchoolModal] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [isLeavingSchool, setIsLeavingSchool] = useState(false);  

  const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Obtenir les années disponibles à partir des factures
  const availableYears = useMemo(() => {
    if (!invoices.length) return [];
    
    const years = invoices.map(invoice => 
      new Date(invoice.created * 1000).getFullYear()
    );
    
    return [...new Set(years)].sort((a, b) => b - a); // Trier par ordre décroissant
  }, [invoices]);

  // Filtrer les factures par année sélectionnée
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => 
      new Date(invoice.created * 1000).getFullYear() === selectedYear
    );
  }, [invoices, selectedYear]);


const originalAmount = Number(subscriptionData?.amount || 0);
const discountedAmount = subscriptionData?.codePromo
  ? originalAmount * (1 - subscriptionData?.codePromo.percent_off / 100)
  : originalAmount;


  const handleLeaveSchool = (school) => {
  setSelectedSchool(school);
  setShowLeaveSchoolModal(true);
};

// Fonction pour confirmer et quitter l'école
const confirmLeaveSchool = async () => {
  setIsLeavingSchool(true);
  try {
    // Ton API call ici
    const response = await fetch(`${BASE_URL}/api/leave/school/${selectedSchool.id}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      await refreshUserData();
      toast({
        title: "École quittée",
        description: `Vous avez quitté ${selectedSchool.school_name} avec succès.`,
      });
    }
  } catch (error) {
    toast({
      title: "Erreur",
      description: "Impossible de quitter l'école.",
      variant: "destructive"
    });
  } finally {
    setIsLeavingSchool(false);
    setShowLeaveSchoolModal(false);
    setSelectedSchool(null);
  }
};

  const isCancel_at_period_end = user && user.subscriptions['data'] && user.subscriptions['data'][0] && user.subscriptions['data'][0].cancel_at_period_end || false;
  if(isCancel_at_period_end) {
    const canceled_at = user.subscriptions['data'][0].canceled_at;
  }

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handlePasswordChange = (e) => {
    const { id, value } = e.target;
    setPasswordData(prevState => ({
      ...prevState,
      [id]: value,
    }));
  };

  const formatDateLocal = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
  
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
      hour12: locale !== 'fr', // 24h en français, AM/PM en anglais
    }).format(date);
  };

  const openAddCouponDialog = () => {
    setIsAddCouponDialogOpen(true);
  };

  const reloadUser = async () => {
    setIsAddCouponDialogOpen(false);
    setIsLoading(true);
    await refreshUserData();
    setIsLoading(false);
  };

  const openChangeCardDialog = async () => {
    setIsLoading(true);
    try {
      console.log('Calling create-setup-intent endpoint...');
      const response = await fetch(`${BASE_URL}/api/create-setup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      console.log('Setup Intent Response:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create setup intent');
      }

      const clientSecret = data.clientSecret;
      console.log('Client Secret:', clientSecret);

      if (!clientSecret) {
        throw new Error('Client secret is missing or invalid');
      }

      setClientSecret(clientSecret);
      setIsChangeCardDialogOpen(true);
    } catch (error) {
      console.error('Error in openChangeCardDialog:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.subscriptions) {
      const subscription = user.subscriptions && user.subscriptions['data'] && user.subscriptions['data'][0] || [];

      const currencySymbols = {
        usd: '$',
        eur: '€',
        cad: 'C$',
        chf: 'CHF',
      };
      const currencyCode = subscription?.currency?.toLowerCase();
      const symbol = currencySymbols[currencyCode] ?? currencyCode?.toUpperCase() ?? 'USD';

      const planName = subscription['plan']?.nickname || 'Premium Plan';
      const amount = (subscription['plan']?.amount / 100).toFixed(2);
      const nextBillingDate = new Date(subscription['current_period_end'] * 1000).toLocaleDateString();
      const codePromo = subscription?.discount?.coupon ?? null;
      

      setSubscriptionData({
        planName,
        amount,
        nextBillingDate,
        currencyCode,
        symbol,
        codePromo
      });
    }
  }, [user]);

  useEffect(() => {
    if (user && user.subscriptions && user.subscriptions['data'].length > 0) {
      const subscription = user.subscriptions['data'][0];

      const currencySymbols = {
        usd: '$',
        eur: '€',
        cad: 'C$',
        chf: 'CHF',
      };
      const currencyCode = subscription && subscription.currency.toLowerCase();
      const symbol = currencySymbols[currencyCode] || currencyCode.toUpperCase();

      const planName = subscription.items.data[0]?.plan?.nickname || 'Premium Plan';
      const amount = (subscription.items.data[0]?.plan?.amount / 100).toFixed(2); 
      const nextBillingDate = new Date(subscription.current_period_end * 1000).toLocaleDateString();
      const codePromo = subscription?.discount?.coupon ?? null;

      setSubscriptionData({
        planName,
        amount,
        nextBillingDate,
        currencyCode,
        symbol,
        codePromo
      });
    }

    if (user && user.is_trialing) {
      const planName = `${t('profile.Trial')} (${t('profile.fullAccess')})`;
      const amount = 0; 
      const nextBillingDate = new Date(user.trial_ends_at).toLocaleDateString();

      setSubscriptionData({
        planName,
        amount,
        nextBillingDate,
      });
    }

    const userInvoices = user.invoices['data'] || [];
    setInvoices(userInvoices);
  }, [user]);

  useEffect(() => {
    const userPaymentMethods = user.stripe_user_method_payment?.data || [];
    setPaymentMethods(userPaymentMethods);
  }, [user]);
  
  const [showSchoolWarning, setShowSchoolWarning] = useState(false);
  const [showCoachCountDialog, setShowCoachCountDialog] = useState(false);
  const [coachCount, setCoachCount] = useState('3');
  const [customCoachCount, setCustomCoachCount] = useState('');
  const [isTypeChange, setIsTypeChange] = useState(false);
  const [showChangeSummary, setShowChangeSummary] = useState(false);
  
  const handleAccountTypeChange = (value: string) => {
    if (value === 'S' && profile.account_type === 'C') {
      setShowSchoolWarning(true);
    } else {
      setProfile({...profile, account_type: value});
    }
  };
  
  const handleWarningConfirm = () => {
    setShowSchoolWarning(false);
    setShowCoachCountDialog(true);
  };
  
  const handleCoachCountSubmit = () => {
    const finalCoachCount = coachCount === 'custom' ? customCoachCount : coachCount;
    setProfile({...profile, account_type: 'S'});
    setShowCoachCountDialog(false);
    setIsTypeChange(true);
    setShowChangeSummary(true);
    
    toast({
      title: "Account type changed",
      description: `Your account has been changed to School with ${finalCoachCount} coaches.`,
    });
  };
  
  const handleCustomCoachCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomCoachCount(e.target.value);
    setCoachCount('custom');
  };

  useEffect(() => {
    if (user) {
      setProfile({
        firstname: user.firstname,
        lastname: user.lastname,
        fullname: (user?.firstname || '') + ' ' + (user?.lastname || ''),
        email: user.email,
        is_active : user.is_active || 0,
        account_type: user.account_type || 'C',
        avatarUrl: user.image_url,
        school_name: user.school_name,
        phone: user.phone || '',
        mobile: user.mobile || '',
        street: user.street || '',
        street_number: user.street_number || '',
        zip_code: user.zip_code || '',
        place: user.place || '',
        parent_name_1: user.parent_name_1 || '',
        parent_name_2: user.parent_name_2 || '',
        mother_email: user.mother_email || '',
        father_email: user.father_email || '',
        mother_phone: user.mother_phone || '',
        father_phone: user.father_phone || '',
      });
    }
  }, [user]);
  
  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const finalCoachCount = coachCount === 'custom' ? customCoachCount : coachCount;
    
    const requestBody = {
      firstname: profile.firstname,
      lastname: profile.lastname,
      email: profile.email,
      phone: profile.phone,
      mobile: profile.mobile,
      street: profile.street,
      street_number: profile.street_number,
      zip_code: profile.zip_code,
      place: profile.place,
      parent_name_1: profile.parent_name_1,
      parent_name_2: profile.parent_name_2,
      mother_email: profile.mother_email,
      father_email: profile.father_email,
      mother_phone: profile.mother_phone,
      father_phone: profile.father_phone,
      account_type: profile.account_type,
      is_type_change: isTypeChange,
      school_name: profile.school_name,
      coach_count: isTypeChange ? parseInt(finalCoachCount) : undefined
    };
    
    const response = await fetch(`${BASE_URL}/api/user/update`, {
      method: 'POST',
      headers: {
        'Accept' : 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });
    
    if (response.ok) {
      setIsTypeChange(false);
      setShowChangeSummary(false);
      await refreshUserData();
      
      toast({
        title: "Profile updated",
        description: t('profile.changeHasBeenApplied'),
      });
      setIsLoading(false);
    } else {
      toast({
        title: t('profile.changeHasBeenApplied'),
        description: t('profile.pleaseRetry'),
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  const ChangeCardForm = ({ onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const { toast } = useToast();
    const { token } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (event) => {
      event.preventDefault();

      if (!stripe || !elements) {
        return;
      }

      setIsSubmitting(true);

      try {
        const { error: submitError } = await elements.submit();

        if (submitError) {
          toast({
            title: 'Error',
            description: submitError.message,
            variant: 'destructive',
          });
          setIsSubmitting(false);
          return;
        }

        const { error, paymentMethod } = await stripe.createPaymentMethod({
          elements,
          params: {
            billing_details: {},
          },
        });

        if (error) {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive',
          });
          setIsSubmitting(false);
          return;
        }
        setIsLoading(true);
        const response = await fetch(`${BASE_URL}/api/account/update-payment-method`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            paymentMethodId: paymentMethod.id,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to update payment method');
        }

        toast({
          title: 'Information',
          description: t('profile.paymentMethodSaved'),
        });
        setIsSubmitting(false);
        onSuccess();
        await refreshUserData();

      } catch (error) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
        setIsSubmitting(false);
      }
    };

    return (
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <PaymentElement />
        </div>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t('common.processing') : t('profile.updatePaymentMethod')}
        </Button>
      </form>
    );
  };

   const getProviderIcon = (provider) => {
    switch (provider) {
      case 'google':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        );
      case 'apple':
        return <AppleIcon size={16} />;
      case 'email':
        return <Key size={16} />;
      default:
        return <User size={16} />;
    }
  };

  const openConfirmCancelPlan = () => {
    setIsCancelPlanDialogOpen(true);
  };

  const openConfirmKeepCancelPlan = () => {
    setKeepIsCancelPlanDialogOpen(true);
  };
  
  const cancelPlanAction = async () => {
    try {
      setIsCancelling(true);
      const response = await fetch(`${BASE_URL}/api/account/cancelPlan`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send invoice');
      }
      
      toast({
        title: t('profile.cancelSuccess'),
        description: t('profile.cancelSuccessDesc'),
      });

      setIsCancelPlanDialogOpen(false);
      await refreshUserData();
      setIsCancelling(false);

    } catch (error) {
      setIsCancelling(false);
      toast({
        title: t('profile.cancelError'),
        description: String(error),
        variant: 'destructive',
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const keepCancelPlanAction = async () => {
    try {
      setIsCancelling(true);
      const response = await fetch(`${BASE_URL}/api/account/keepCancelPlan`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send invoice');
      }
      
      toast({
        title: t('profile.noCancelSuccess'),
        description: t('profile.noCancelSuccessDesc'),
      });

      setIsCancelPlanDialogOpen(false);
      await refreshUserData();
      setIsCancelling(false);

    } catch (error) {
      setIsCancelling(false);
      toast({
        title: t('profile.cancelError'),
        description: String(error),
        variant: 'destructive',
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const { data: sessionsData = [], isLoading: sessionsLoading, refetch } = useQuery({
    queryKey: ['sessions', token],
    queryFn: async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/sessions/get`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch invoices');
        }
        const data = await response.json();

        return data.sessions || [];
      } catch (error) {
        console.error('Error fetching invoices:', error);
        return [];
      }
    },
  });

  const handleLogoutSession = async (tokenId) => {
    logoutToken(tokenId).then(() => {
      toast({
        title: t('profile.sessionLoggedOut'),
        description: t('profile.sessionLoggedOutDesc'),
      });
      refetch();
    });
  };

  const handleLogoutAllSessions = async () => {
    await logoutAll();
  };
  
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Information",
        description: t('profile.newPasswordNotMatch'),
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/user/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      toast({
        title: t('profile.passwordUpdated'),
        description: t('profile.yourPasswordChanged'),
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setIsLoading(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'An error occurred while changing the password.',
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };
  
  const handleSaveNotifications =  async (e) => {

    e.preventDefault();
    setIsSavingNotification(true);

    try {
      const response = await fetch(`${BASE_URL}/api/account/update/notifications-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      toast({
        title: t('settings.notificationsSettingsSaved'),
        description: t('settings.notificationsSettingsSavedDesc'),
      });

      await refreshUserData();
      setIsSavingNotification(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'An error occurred while changing the password.',
        variant: "destructive",
      });
      setIsSavingNotification(false);
    }    
  };
  
  const handleBillingAction = (action: string) => {
    toast({
      title: `${action} successful`,
      description: `Your billing ${action.toLowerCase()} has been processed.`,
    });
  };

  const handleLogout = () => {
    logout();
  };

  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);

  const handleAvatarUpdate = (newAvatarUrl: string) => {
    setProfile(prev => ({ ...prev, avatarUrl: newAvatarUrl }));
  };

  const handleDeleteAccountRequest = async () => {
    setIsDeletingAccount(true);
    try {
      const response = await fetch(`${BASE_URL}/api/user/ask_delete_account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to request account deletion');
      }

      toast({
        title: t('deleteAccount.requestSent'),
        description: t('deleteAccount.emailConfirmation'),
      });
      setIsDeleteAccountModalOpen(false);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: String(error),
        variant: 'destructive',
      });
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 container py-8 mt-16">
        <DeleteAccountModal
            isOpen={isDeleteAccountModalOpen}
            onClose={() => setIsDeleteAccountModalOpen(false)}
            onConfirm={handleDeleteAccountRequest}
            isDeleting={isDeletingAccount}
        />
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8">
          {/* Mobile-only tab selector */}
          <div className="md:hidden">
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <div
                    className="cursor-pointer"
                    onClick={() => setIsAvatarModalOpen(true)}
                  >
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={profile.avatarUrl} alt={profile.fullname || ''} />
                      <AvatarFallback>
                        {profile.account_type === 'S'
                          ? (profile.school_name || '').split(' ').map(n => n[0]).join('')
                          : (profile.fullname || '').split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base font-semibold truncate">
                      {profile.account_type === 'S' ? profile.school_name : profile.fullname}
                    </h2>
                    <p className="text-xs text-muted-foreground truncate">
                      {profile.account_type === 'C' && user.roleType === 'teachers_admin' && t('accounts.coachAccount')}
                      {profile.account_type === 'C' && user.roleType === 'teachers_all' && t('accounts.schoolTeacherAccount')}
                      {profile.account_type === 'S' && t('accounts.schoolAccount')}
                      {profile.account_type === 'Student' && t('accounts.studentAccount')}
                      {profile.account_type === 'Parent' && t('accounts.parentAccount')}
                    </p>
                  </div>
                </div>

                <Select
                  value={activeTab}
                  onValueChange={(value) => {
                    if (value === '__logout__') {
                      handleLogout();
                      return;
                    }
                    setActiveTab(value);
                  }}
                >
                  <SelectTrigger className="w-full h-11">
                    <SelectValue placeholder={t('profile.Tabs.profile')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="profile">
                      <span className="flex items-center">
                        <User className="mr-2 h-4 w-4" />
                        {t('profile.Tabs.profile')}
                      </span>
                    </SelectItem>
                    {profile.account_type !== 'Student' && profile.account_type !== 'Parent' && user.is_subscribed && (
                      <SelectItem value="billing">
                        <span className="flex items-center">
                          <CreditCard className="mr-2 h-4 w-4" />
                          {t('profile.Tabs.subscritpion')}
                        </span>
                      </SelectItem>
                    )}
                    {(profile.account_type === 'S' || profile.account_type === 'C' || profile.account_type === 'Student') && (
                      <SelectItem value="invitations">
                        <span className="flex items-center">
                          <Link2 className="mr-2 h-4 w-4" />
                          {profile.account_type === 'S' ? t('profile.Tabs.invitation') : t('profile.Tabs.join')}
                        </span>
                      </SelectItem>
                    )}
                    {profile.account_type === 'C' && (
                      <SelectItem value="referrals">
                        <span className="flex items-center">
                          <Gift className="mr-2 h-4 w-4" />
                          {t('profile.Tabs.referral')}
                        </span>
                      </SelectItem>
                    )}
                    <SelectItem value="notifications">
                      <span className="flex items-center">
                        <Bell className="mr-2 h-4 w-4" />
                        {t('profile.Tabs.notifications')}
                      </span>
                    </SelectItem>
                    <SelectItem value="security">
                      <span className="flex items-center">
                        <Shield className="mr-2 h-4 w-4" />
                        {t('profile.Tabs.security')}
                      </span>
                    </SelectItem>
                    <SelectItem value="sessions">
                      <span className="flex items-center">
                        <Clock className="mr-2 h-4 w-4" />
                        {t('profile.Tabs.sessions')}
                      </span>
                    </SelectItem>
                    {showHelpTab && (
                      <SelectItem value="help">
                        <span className="flex items-center">
                          <HelpCircle className="mr-2 h-4 w-4 text-primary" />
                          {t('profile.Tabs.help') || 'Aide'}
                        </span>
                      </SelectItem>
                    )}
                    <SelectSeparator />
                    <SelectItem value="__logout__" className="text-destructive focus:text-destructive">
                      <span className="flex items-center">
                        <LogOut className="mr-2 h-4 w-4" />
                        {t('profile.Tabs.logout')}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          </div>

          <Card className="h-fit hidden md:block">
            <CardContent className="p-6">
              <div className="flex flex-col items-center space-y-4 mb-6">
                <div 
                  className="cursor-pointer" 
                  onClick={() => setIsAvatarModalOpen(true)}
                >
                  <Avatar className="h-24 w-24 hover:opacity-80 transition-opacity">
                    <AvatarImage src={profile.avatarUrl} alt={profile.fullname || ''} />
                    <AvatarFallback>
                      {profile.account_type === 'S'
                        ? (profile.school_name || '')
                            .split(' ')
                            .map(n => n[0])
                            .join('')
                        : (profile.fullname || '')
                            .split(' ')
                            .map(n => n[0])
                            .join('')}
                    </AvatarFallback>
                  </Avatar>

                </div>
                <div className="text-center">
                  <h2 className="text-xl font-bold">{profile.account_type === 'S' ? profile.school_name : profile.fullname}</h2>
                  <p className="text-sm text-muted-foreground">
                    {profile.account_type === 'C' && user.roleType === 'teachers_admin' && t('accounts.coachAccount')}
                    {profile.account_type === 'C' && user.roleType === 'teachers_all' && t('accounts.schoolTeacherAccount')}
                    {profile.account_type === 'S' && t('accounts.schoolAccount')}
                    {profile.account_type === 'Student' && t('accounts.studentAccount')}
                    {profile.account_type === 'Parent' && t('accounts.parentAccount')}
                    {['C', 'S', 'Student', 'Parent'].includes(profile.account_type) === false && t('accounts.unknownAccount')}
                  </p>

                  {user && user.is_active === 0 && (
                    <Badge variant="destructive" className="mt-2">Account not validated</Badge>
                  )}
                </div>
              </div>
              
              <div className="space-y-1">
                <Button 
                  variant={activeTab === 'profile' ? "default" : "ghost"} 
                  className="w-full justify-start"
                  onClick={() => setActiveTab('profile')}
                >
                  <User className="mr-2 h-4 w-4" />
                  {t('profile.Tabs.profile')}
                </Button>
                
                {profile.account_type !== 'Student' && profile.account_type !== 'Parent' && user.is_subscribed && (
                  <Button 
                    variant={activeTab === 'billing' ? "default" : "ghost"} 
                    className="w-full justify-start relative"
                    onClick={() => setActiveTab('billing')}
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    {t('profile.Tabs.subscritpion')}

                    {(!user.is_subscribed && !user.is_trialing) && (
                      <div className="absolute top-1 right-1 flex items-center justify-center">
                        <div className="relative">
                          <Info className="h-2 w-2 text-red-300" />
                        </div>
                      </div>
                    )}
                  </Button>
                )}

                {/*profile.account_type === 'C' && (
                  <Button
                    variant={activeTab === 'join' ? "default" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => setActiveTab('join')}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Invitation
                  </Button>
                )*/}

                {(profile.account_type === 'S' || profile.account_type === 'C' || profile.account_type === 'Student') && (
                  <Button
                    variant={activeTab === 'invitations' ? "default" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => setActiveTab('invitations')}
                  >
                    <Link2 className="mr-2 h-4 w-4" />
                    {profile.account_type === 'S' ? t('profile.Tabs.invitation') : t('profile.Tabs.join')}
                  </Button>
                )}


                {(profile.account_type === 'C') && (
                  <Button 
                    variant={activeTab === 'referrals' ? "default" : "ghost"} 
                    className="w-full justify-start"
                    onClick={() => setActiveTab('referrals')}
                  >
                    <Gift className="mr-2 h-4 w-4" />
                    {t('profile.Tabs.referral')}
                  </Button>
                )}

                <Button 
                  variant={activeTab === 'notifications' ? "default" : "ghost"} 
                  className="w-full justify-start"
                  onClick={() => setActiveTab('notifications')}
                >
                  <Bell className="mr-2 h-4 w-4" />
                  {t('profile.Tabs.notifications')}
                </Button>

                <Button 
                  variant={activeTab === 'security' ? "default" : "ghost"} 
                  className="w-full justify-start"
                  onClick={() => setActiveTab('security')}
                >
                  <Shield className="mr-2 h-4 w-4" />
                  {t('profile.Tabs.security')}
                </Button>
                <Button 
                  variant={activeTab === 'sessions' ? "default" : "ghost"} 
                  className="w-full justify-start"
                  onClick={() => setActiveTab('sessions')}
                >
                  <Clock className="mr-2 h-4 w-4" />
                  {t('profile.Tabs.sessions')}
                </Button>
                {showHelpTab && (
                  <Button
                    variant={activeTab === 'help' ? "default" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => setActiveTab('help')}
                  >
                    <HelpCircle className="mr-2 h-4 w-4" />
                    {t('profile.Tabs.help') || 'Aide'}
                  </Button>
                )}
              </div>
              
              <div className="mt-6 pt-6 border-t">
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('profile.Tabs.logout')}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <div>
  {activeTab === 'profile' && (
    <Card className="border-0 rounded-none shadow-none p-1 md:border md:rounded-xl md:shadow-sm md:p-0">
      <CardHeader className="p-0 md:p-6 hidden md:block">
        <CardTitle>{t('profile.title')}</CardTitle>
      </CardHeader>
                
            {user && user.is_active === 0 && (
              <CardContent className="pt-6">
                <div className="relative overflow-hidden bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 rounded-lg p-6 shadow-sm">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-amber-100 rounded-full -mr-16 -mt-16 opacity-30"></div>
                  <div className="relative z-10">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-amber-800 mb-2">
                          Validation du compte requise
                        </h3>
                        <p className="text-amber-700 mb-4 leading-relaxed">
                          Votre compte doit être validé avant de pouvoir accéder à toutes les fonctionnalités.
                        </p>
                        <Button 
                          className="bg-amber-500 hover:bg-amber-600 text-white border-0 shadow-md hover:shadow-lg transition-all duration-200"
                          onClick={() => navigate('/verify')}
                        >
                          <Shield className="mr-2 h-4 w-4" />
                          Aller à la vérification
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
          )}

          {/* Informations du compte actif */}
          {user && user.is_active === 1 && (
            <CardContent className="p-0 md:p-6 md:pt-0">
              {/* Carte d'informations utilisateur */}
              <div className="bg-none p-0 border-0 rounded-none shadow-none md:bg-gradient-to-br md:from-blue-50 md:to-emerald-50 md:rounded-xl md:p-6 md:mb-6 md:border md:border-green-100 md:shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md border border-green-100">
                      {getProviderIcon(user.provider)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{user.username}</h3>
                        <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Actif
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        Connexion via {user.provider === 'google' ? 'Google' : user.provider === 'apple' ? 'Apple' : 'Email'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Informations école pour les enseignants */}
                {/*profile.account_type === 'C' && user.roleType === 'teachers_all' && (
                  <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-green-100">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <School className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">{t('profile.teacherConnectedToSchool')}</p>
                        <p className="font-semibold text-gray-900">{profile.school_name}</p>
                      </div>
                    </div>
                  </div>
                )*/}

                {/* Informations école pour les étudiants */}
                  {(profile.account_type === 'Student' || profile.account_type === 'Parent') && (
                    <div className="space-y-3">
                      {user.schools?.map((school) => (
                        <div
                          key={school.id}
                          className="bg-white rounded-xl p-4 shadow-sm border border-green-100 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between gap-3">
                            {/* Contenu principal de l'école */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                                {school.school_type === 'S' ? (
                                  <School className="h-5 w-5 text-blue-600" />
                                ) : (
                                  <GraduationCap className="h-5 w-5 text-blue-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500 mb-1">
                                  {school.school_type === 'S' ? t('profile.teacherConnectedToSchool') : t('profile.teacherConnectedToCoach')}
                                </p>
                                <p className="font-semibold text-blue-700 text-sm leading-tight truncate">
                                  {school.school_name}
                                </p>
                              </div>
                            </div>
                            
                            {profile.account_type === 'Student' && user?.school_id != school.id && (
                              <button
                                onClick={() => handleLeaveSchool(school)}
                                className="flex-shrink-0 w-9 h-9 bg-red-50 hover:bg-red-100 rounded-full flex items-center justify-center transition-colors group"
                                aria-label={`Quitter ${school.school_name}`}
                              >
                                <X className="h-4 w-4 text-red-500 group-hover:text-red-600" />
                              </button>
                            )}

                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                {profile.account_type === 'C' && user?.schools.length > 0 && (
                  <div className="space-y-3">
                    {user.schools?.map((school) => (
                      <div
                        key={school.id}
                        className="bg-white rounded-xl p-4 shadow-sm border border-green-100 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center justify-between gap-3">
                          {/* Contenu principal de l'école */}
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                              {school.school_type === 'S' ? (
                                <School className="h-5 w-5 text-blue-600" />
                              ) : (
                                <GraduationCap className="h-5 w-5 text-blue-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500 mb-1">
                                {school.school_type === 'S' ? t('profile.teacherConnectedToSchool') : t('profile.teacherConnectedToCoach')}
                              </p>
                              <p className="font-semibold text-blue-700 text-sm leading-tight truncate">
                                {school.school_name}
                              </p>
                            </div>
                          </div>
                          
                          {/* Bouton quitter */}
                          {user?.school_id != school.id && (
                          <button
                            onClick={() => handleLeaveSchool(school)}
                            className="flex-shrink-0 w-9 h-9 bg-red-50 hover:bg-red-100 rounded-full flex items-center justify-center transition-colors group"
                            aria-label={`Quitter ${school.school_name}`}
                          >
                            <X className="h-4 w-4 text-red-500 group-hover:text-red-600" />
                          </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}


                {/* Informations localisation et devise */}
                {profile.account_type !== 'Student' && profile.account_type !== 'Parent' && (
                  <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mt-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-green-100 min-w-0">
                      <div className="flex items-center gap-3 min-w-0">
                        {/* Icône fixe */}
                        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center shrink-0">
                          <MapPin className="h-4 w-4 text-indigo-600" />
                        </div>
                        
                        {/* Conteneur de texte */}
                        <div className="min-w-0 flex-1 flex flex-col">
                          {/* Code pays au-dessus */}
                          <span className="text-xs text-gray-600 uppercase tracking-wider">
                            {user.teacher.country_code}
                          </span>
                          {/* Timezone en-dessous (tronqué si trop long) */}
                          <p 
                            className="text-gray-900 text-xs truncate font-medium mt-0.5" 
                            title={user.timezone}
                          >
                            {user.timezone}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-green-100">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                          <HandCoins className="h-4 w-4 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">{t('agenda.currency')}</p>
                          <p className="text-gray-900 text-xs">{user.currency_code}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          )}
          
          {/* Formulaire */}
          <CardContent className="p-0 pt-8 md:p-6 md:pt-0">
              <b>{t('profile.subTitle')}</b>

            <div className="space-y-6 pt-6">
              {/* Nom d'entreprise pour les écoles */}
              {profile.account_type === 'S' && user.roleType === 'school_admin' && (
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-sm font-medium text-gray-700">
                    {t('profile.companyName')}
                  </Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      id="companyName" 
                      className="pl-10 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      value={profile.school_name}
                      onChange={(e) => setProfile({...profile, school_name: e.target.value})}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Prénom */}
                {user.account_type !== "Parent" && (
                  <div className="space-y-2">
                    <Label htmlFor="firstname" className="text-sm font-medium text-gray-700">
                      {t('profile.firstname')}
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="firstname"
                        className="pl-10 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                        value={profile.firstname}
                        onChange={(e) => setProfile({ ...profile, firstname: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                {/* Nom */}
                <div className="space-y-2">
                  <Label htmlFor="lastname" className="text-sm font-medium text-gray-700">
                    {t('profile.lastname')}
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="lastname"
                      className="pl-10 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      value={profile.lastname}
                      onChange={(e) => setProfile({ ...profile, lastname: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  {t('profile.email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    className="pl-10 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  />
                </div>
                </div>




              {profile.account_type === 'Student' && (
                <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-gray-700">{t('students.phone')}</Label>
                    <Input
                      id="phone"
                      type="text"
                      name="phone"
                      value={profile.phone || ''}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mobile" className="text-sm font-medium text-gray-700">{t('students.mobile')}</Label>
                    <Input
                      id="mobile"
                      type="text"
                      name="mobile"
                      value={profile.mobile || ''}
                        onChange={(e) => setProfile({ ...profile, mobile: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>



                <div className='bg-gray-50 p-3 rounded-sm'>
                  <h5 className="text-sm text-gray-800 mb-4 pt-4 flex items-center">
                    <MapPin className="mr-2 h-5 w-5" />
                    <strong>{t('students.billingAddress')}</strong>
                  </h5>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="street" className="text-sm font-medium text-gray-700">{t('students.street')}</Label>
                        <Input
                          id="street"
                          name="street"
                          value={profile.street || ''}
                            onChange={(e) => setProfile({ ...profile, street: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="street_number" className="text-sm font-medium text-gray-700">{t('students.streetNumber')}</Label>
                        <Input
                          id="street_number"
                          name="street_number"
                          value={profile.street_number || ''}
                            onChange={(e) => setProfile({ ...profile, street_number: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="zip_code" className="text-sm font-medium text-gray-700">{t('students.zipCode')}</Label>
                        <Input
                          id="zip_code"
                          name="zip_code"
                          value={profile.zip_code || ''}
                            onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="place" className="text-sm font-medium text-gray-700">{t('students.place')}</Label>
                        <Input
                          id="place"
                          name="place"
                          value={profile.place || ''}
                            onChange={(e) => setProfile({ ...profile, place: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>  



                {/* Parent Information */}
                       <div className='bg-gray-50 p-3 rounded-sm'>
                          <h3 className="text-sm text-gray-800 mb-4 pt-4 flex items-center">
                            <Users className="mr-2 h-5 w-5" />
                            <strong>{t('students.parentInfo')}</strong>
                          </h3>
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Colonne mère */}
                              <div className="space-y-4">
                                <div>
                                  <Label htmlFor="parent_name_1" className="text-sm font-medium text-gray-700">{t('students.motherName')}</Label>
                                  <Input
                                    id="parent_name_1"
                                    name="parent_name_1"
                                    type="text"
                                    value={profile.parent_name_1 || ''}
                                     onChange={(e) => setProfile({ ...profile, parent_name_1: e.target.value })}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="mother_email" className="text-sm font-medium text-gray-700">{t('students.motherEmail')}</Label>
                                  <Input
                                    id="mother_email"
                                    name="mother_email"
                                    type="email"
                                    value={profile.mother_email || ''}
                                    onChange={(e) => setProfile({ ...profile, mother_email: e.target.value })}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="mother_phone" className="text-sm font-medium text-gray-700">{t('students.motherPhone')}</Label>
                                  <Input
                                    id="mother_phone"
                                    name="mother_phone"
                                    type="number"
                                    value={profile.mother_phone || ''}
                                    onChange={(e) => setProfile({ ...profile, mother_phone: e.target.value })}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                
                              {/* Colonne père */}
                              <div className="space-y-4">
                                <div>
                                  <Label htmlFor="parent_name_2" className="text-sm font-medium text-gray-700">{t('students.fatherName')}</Label>
                                  <Input
                                    id="parent_name_2"
                                    name="parent_name_2"
                                    type="text"
                                    value={profile.parent_name_2 || ''}
                                    onChange={(e) => setProfile({ ...profile, parent_name_2: e.target.value })}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="father_email" className="text-sm font-medium text-gray-700">{t('students.fatherEmail')}</Label>
                                  <Input
                                    id="father_email"
                                    name="father_email"
                                    type="email"
                                    value={profile.father_email || ''}
                                    onChange={(e) => setProfile({ ...profile, father_email: e.target.value })}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="father_phone" className="text-sm font-medium text-gray-700">{t('students.fatherPhone')}</Label>
                                  <Input
                                    id="father_phone"
                                    name="father_phone"
                                    type="number"
                                    value={profile.father_phone || ''}
                                    onChange={(e) => setProfile({ ...profile, father_phone: e.target.value })}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                </>
              )}


              
              {/* Résumé des changements */}
              {showChangeSummary && profile.account_type === 'S' && isTypeChange && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Résumé des modifications
                  </h3>
                  <div className="flex items-center gap-3 mb-3">
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                      Compte École
                    </Badge>
                    <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                      {coachCount === 'custom' ? customCoachCount : coachCount} Coachs
                    </Badge>
                  </div>
                  <p className="text-blue-700 leading-relaxed">
                    Votre type de compte sera modifié en École avec {coachCount === 'custom' ? customCoachCount : coachCount} coachs.
                  </p>
                </div>
              )}
              
              {/* Bouton de sauvegarde */}
              <div className="flex justify-center md:justify-end pt-4">
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-blue-600 to-blue-400 hover:from-blue-500 hover:to-blue-600 text-white px-8 py-2 shadow-lg transition-all duration-200"
                  disabled={isLoading}
                  onClick={handleProfileUpdate}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.processing')}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t('profile.save')}
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'join' && (profile.account_type === 'S' || profile.account_type === 'C' || profile.account_type === 'Student') && (
         <Card>
          <CardContent className='p-0'>
            <InvitationJoinStudentManager
                userType="school"
                onTabChange={setActiveTab}
                onShowSuccessModal={setShowSuccessModal} // Passer la fonction pour gérer la modal
              />
            </CardContent>
          </Card>
      )}
            
            {activeTab === 'invitations' && (profile.account_type === 'S' || profile.account_type === 'C' || profile.account_type === 'Student') && (
              <Card>
                {/*<CardHeader>
                  {profile.account_type === 'S' && (
                  <CardTitle>
                    {profile.account_type === 'S' 
                          ? "Invitations"
                          : ""
                    }
                  </CardTitle>
                  )}
                  <CardDescription>
                    {profile.account_type === 'S' 
                      ? t('invitations.managerCoachInvitation')
                      : ""
                    }
                  </CardDescription>
                </CardHeader>*/}
                <CardContent className='p-0'>
                  {user?.account_type === "Student" ? (
                  <InvitationStudentManager
                    userType="student"
                    onTabChange={setActiveTab}
                    onShowSuccessModal={setShowSuccessModal} // Passer la fonction pour gérer la modal
                  />
                  ):
                  (
                  <InvitationManager
                    userType={profile.account_type === 'S' ? 'school' : 'coach'}
                    onTabChange={setActiveTab}
                    onShowSuccessModal={setShowSuccessModal} // Passer la fonction pour gérer la modal
                  />
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'referrals' && (profile.account_type === 'S' || profile.account_type === 'C') && (
              <Card className="border-0 rounded-none shadow-none p-1 md:border md:rounded-xl md:shadow-sm md:p-0">
                <CardHeader className="p-0 md:p-6 hidden md:block">
                  <CardTitle>{t('referral.title')}</CardTitle>
                </CardHeader>
                <CardContent className="p-0 md:p-6 md:pt-0">
                  <ReferralManager />
                </CardContent>
              </Card>
            )}
            
            {activeTab === 'security' && (
              <Card className="border-0 rounded-none shadow-none p-1 md:border md:rounded-xl md:shadow-sm md:p-0">
              <CardHeader className="p-0 md:p-6 hidden md:block">
                  <CardTitle>{t('profile.Tabs.security')}</CardTitle>
                  <CardDescription>{t('profile.security_subTitle')}</CardDescription>
                </CardHeader>

                <CardContent className="p-0 md:p-6 md:pt-0 mb-5">
                  <TwoFactorSection />
                </CardContent>

                {user && user.is_active === 1 && (
                  <CardContent className="p-0 md:p-6 md:pt-0 mb-5">
                    <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-2 md:mb-0 text-sm flex items-center space-x-2">
                      {user && user.provider === "google" && (
                        <span className="flex items-center text-green-600 space-x-1">
                          <svg width="16" height="16" viewBox="0 0 24 24">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                        </span>
                      )}
                      {user && user.provider === "apple" && (
                        <span className="flex items-center text-green-600 space-x-1">
                          <AppleIcon size={16} />
                        </span>
                      )}
                      {user && user.provider === "email" && (
                        <span className="flex items-center text-green-600 space-x-1">
                          <Key size={16} />
                        </span>
                      )}
                      <span>{t('profile.connected')}: {user.username}</span>
                    </div>
                  </CardContent>
                )}

                {user && (user.provider === "apple" || user.provider === "google") && (
                  <div>
                    <CardContent className="p-0 md:p-6 md:pt-0">
                      <div className="border border-red-200 rounded-md p-4 mb-2 text-sm flex items-center space-x-2">
                        {t('profile.noChangePassword')}.
                      </div>
                    </CardContent>
                  </div>
                )}

                <CardContent className="p-0 md:p-6 md:pt-0">
                  {user && user.provider === "email" && (
                    <form onSubmit={handlePasswordUpdate} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="currentPassword">{t('profile.currentPassword')}</Label>
                        <Input
                          id="currentPassword"
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newPassword">{t('profile.newPassword')}</Label>
                        <Input
                          id="newPassword"
                          type="password"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">{t('profile.confirmNewPassword')}</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                        />
                      </div>
                      <Button type="submit">
                        {isLoading ? (
                          <span className="flex items-center">
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            {t('common.processing')}
                          </span>
                        ) : (
                          t('profile.changePassword')
                        )}
                      </Button>
                    </form>
                  )}
                  
                </CardContent>
                {profile && (profile?.account_type === "S" ||  profile?.account_type === "C") ? (
                  <CardContent className="p-0 m-0 md:p-6 md:pt-0 md:p-3 md:m-4 p-4 bg-red-50 mt-5 rounded-md opacity-50 hover:opacity-100">
                    <div className="p-1">
                        <h3 className="text-lg font-medium">{t('deleteAccount.deleteMyAccount')}</h3>
                        <p className="text-sm text-muted-foreground mb-4">{t('deleteAccount.deleteAccountWarning')}</p>
                        <Button variant="outline" className="text-white bg-red-400 hover:bg-red-600 hover:text-white" onClick={() => setIsDeleteAccountModalOpen(true)}>
                            {t('deleteAccount.deleteMyAccount')}
                        </Button>
                    </div>
                </CardContent>
                ):(null)}
              </Card>
            )}
            
            {activeTab === 'billing' && (
             <Card className="border-0 rounded-none shadow-none p-1 md:border md:rounded-xl md:shadow-sm md:p-0">
              <CardHeader className="p-0 md:p-6 hidden md:block">
                  <CardTitle>{t('profile.Tabs.subscritpion')}</CardTitle>
                  <CardDescription>{t('profile.subscription_subTitle')}</CardDescription>
                </CardHeader>
                <CardContent className="p-0 md:p-6 md:pt-0">
                  <div className="space-y-5">
                    <div>
                      <div className={isCancel_at_period_end ? "bg-red-100 p-6 rounded-xl" : "bg-primary/5 p-6 rounded-xl"}>
                        {user.is_subscribed || user.is_trialing ? (
                          <div className="space-y-6">
                            {/* Header avec badge de statut */}
                            <div className={`items-start justify-between ${!isMobile ? 'flex' : ''}`}>
                              <div className="flex items-start space-x-4">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                                  user.is_trialing 
                                    ? 'bg-gradient-to-tr from-amber-500 to-orange-500' 
                                    : isCancel_at_period_end 
                                      ? 'bg-gradient-to-tr from-red-500 to-red-600'
                                      : 'bg-gradient-to-tr from-blue-500 to-blue-600'
                                }`}>
                                  {user.is_trialing ? (
                                    <Timer className="w-7 h-7 text-white" />
                                  ) : isCancel_at_period_end ? (
                                    <AlertTriangle className="w-7 h-7 text-white" />
                                  ) : (
                                    <Crown className="w-7 h-7 text-white" />
                                  )}
                                </div>
                                
                                <div className="flex-1">
                                 <div className={`items-center space-x-1 mb-2 ${!isMobile ? 'flex' : ''}`}>
                                    <h4 className="text-xl font-bold">
                                      {subscriptionData?.planName || t('profile.premiumPlan')}
                                    </h4>
                                    {!isMobile && (
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                      user.is_trialing 
                                        ? 'bg-amber-100 text-amber-800' 
                                        : isCancel_at_period_end
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-blue-100 text-blue-800'
                                    }`}>
                                      {user.is_trialing ? t('profile.Trial') : isCancel_at_period_end ? t('profile.willBeCancel') : t('profile.active')}
                                    </span>
                                    )}
                                  </div>
                                  
                                  <div className={isMobile ? "items-center mb-3 gap-2" : "flex items-center mb-3 gap-2"}>
                                    {originalAmount === 0 ? (
                                      <p className="text-lg font-bold">{t('profile.free')}</p>
                                    ) : (
                                      <>
                                        {/* Prix barré si code promo */}
                                        {subscriptionData?.codePromo ? (
                                          <>
                                            <span className="text-lg font-bold text-gray-400 line-through">
                                              {`${subscriptionData?.symbol || '$'}${originalAmount.toFixed(2)}`}
                                            </span>
                                            <span className="text-lg font-bold text-blue-600">
                                              {`${subscriptionData?.symbol || '$'}${discountedAmount.toFixed(2)}`}
                                            </span>
                                          </>
                                        ) : (
                                          <span className="text-lg font-bold">
                                            {`${subscriptionData?.symbol || '$'}${originalAmount.toFixed(2)}`}
                                          </span>
                                        )}
                                      </>
                                    )}

                                    {originalAmount > 0 && (
                                      <span className="text-md text-muted-foreground font-medium">
                                        {t('profile.perMonth')}
                                        {subscriptionData?.codePromo && (
                                          <Badge className={`text-xs ${!isMobile ? 'ml-1' : ''} bg-green-50 border-green-100`}>
                                            {t('profile.discount')} -{subscriptionData?.codePromo.percent_off}%
                                          </Badge>
                                        )}
                                      </span>
                                    )}
                                  </div>



                                  
                                </div>
                              </div>
                              
                              {/* Actions */}
                              <div className={isMobile ? "w-full mt-4" : ""}>
                                {user?.is_subscribed ? (
                                  <Button
                                    disabled={isCancelling}
                                    variant={isCancel_at_period_end ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => isCancel_at_period_end ? openConfirmKeepCancelPlan() : openConfirmCancelPlan()}
                                    className={`${isMobile ? "w-full" : ""} ${
                                      isCancel_at_period_end 
                                        ? "bg-white hover:bg-green-400 text-dark hover:text-white" 
                                        : "hover:bg-red-50 hover:border-red-200 hover:text-red-600"
                                    }`}>

                                    {isCancelling ? (
                                      <span className="flex items-center space-x-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>{t('common.processing')}</span>
                                      </span>
                                    ) : (
                                      <span className="flex items-center space-x-2">
                                        {isCancel_at_period_end ? (
                                          <>
                                            <RotateCcw className="w-4 h-4" />
                                            <span>{t('profile.noCancel')}</span>
                                          </>
                                        ) : (
                                          <>
                                            <X className="w-4 h-4" />
                                            <span>{t('profile.cancelSubscription')}</span>
                                          </>
                                        )}
                                      </span>
                                    )}
                                  </Button>
                                ) : user?.is_trialing ? (
                                  <Button
                                    className={`${isMobile ? "w-full" : ""} bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white`}
                                    size="sm"
                                    onClick={() => navigate('/subscription')}
                                  >
                                    <span className="flex items-center space-x-2">
                                      <Zap className="w-4 h-4" />
                                      <span>{t('profile.changePlan')}</span>
                                    </span>
                                  </Button>
                                ) : null}
                              </div>
                            </div>

                            {/* Informations de facturation avec design amélioré */}
                            <div className={`p-4 rounded-xl border-2 ${
                              user.is_trialing 
                                ? 'bg-amber-50 border-amber-200' 
                                : isCancel_at_period_end
                                  ? 'bg-red-50 border-red-200'
                                  : 'bg-green-50 border-green-200'
                              }`}>
                              <div className="flex items-center space-x-3">
                                <div className={`w-2 h-2 rounded-full ${
                                  user.is_trialing ? 'bg-amber-500' : isCancel_at_period_end ? 'bg-red-500' : 'bg-green-500'
                                }`}></div>
                                <p className={`text-sm font-medium ${
                                  user.is_trialing ? 'text-amber-800' : isCancel_at_period_end ? 'text-red-800' : 'text-green-800'
                                }`}>
                                  {user.is_trialing ? (
                                    <>
                                      {t('profile.your_trial_ends')} <strong>{subscriptionData?.nextBillingDate || 'June 15, 2023'}</strong>
                                    </>
                                  ) : (
                                    <>
                                      {isCancel_at_period_end ? t('profile.cancelSuccessDesc') : t('profile.your_next_billing_date')} 
                                      <strong className='ml-1'>{subscriptionData?.nextBillingDate || 'June 15, 2023'}</strong>
                                    </>
                                  )}
                                </p>
                              </div>
                            </div>



                            {subscriptionData?.codePromo && subscriptionData.codePromo.valid  && (
                              <div className={`p-4 rounded-xl border-2 bg-green-50 border-green-200`}>
                                <div className="flex items-center space-x-3">
                                  {!isMobile && (
                                  <div className={`w-2 h-2 rounded-full ${
                                    user.is_trialing ? 'bg-amber-500' : isCancel_at_period_end ? 'bg-red-500' : 'bg-green-500'
                                  }`}></div>
                                  )}
                                  <p className={`text-sm font-medium ${
                                    user.is_trialing ? 'text-amber-800' : isCancel_at_period_end ? 'text-red-800' : 'text-green-800'
                                  }`}>
                                    <>
                                        {t('profile.codePromo', {percent_off: subscriptionData?.codePromo.percent_off})}
                                      </>
                                  </p>
                                </div>
                              </div>
                            )}



                          </div>
                        ) : (
                          <div className="space-y-8">
                            {/* Header section */}
                            <div className="text-center">
                              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center">
                                <CreditCard className="w-8 h-8 text-white" />
                              </div>
                              <h4 className="text-2xl font-bold mb-2">{t('profile.noActiveSubscription')}</h4>
                              <p className="text-muted-foreground mb-6">{t('profile.noActiveSubscriptionExplication')}.</p>
                            </div>

                            {/* Features grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="flex items-start space-x-3 p-4 rounded-lg bg-blue-50 border border-blue-100">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-500 to-blue-400 flex items-center justify-center flex-shrink-0">
                                  <Dumbbell className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <h5 className="font-semibold text-sm mb-1">{t('features.smartScheduling.title')}</h5>
                                  <p className="text-xs text-muted-foreground">{t('features.smartScheduling.description')}</p>
                                </div>
                              </div>

                              <div className="flex items-start space-x-3 p-4 rounded-lg bg-purple-50 border border-purple-100">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-purple-500 to-purple-400 flex items-center justify-center flex-shrink-0">
                                  <Users className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <h5 className="font-semibold text-sm mb-1">{t('features.studentManagement.title')}</h5>
                                  <p className="text-xs text-muted-foreground">{t('features.studentManagement.description')}</p>
                                </div>
                              </div>

                              <div className="flex items-start space-x-3 p-4 rounded-lg bg-pink-50 border border-pink-100">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-pink-500 to-pink-400 flex items-center justify-center flex-shrink-0">
                                  <Award className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <h5 className="font-semibold text-sm mb-1">{t('features.automaticInvoices.title')}</h5>
                                  <p className="text-xs text-muted-foreground">{t('features.automaticInvoices.description')}</p>
                                </div>
                              </div>

                              <div className="flex items-start space-x-3 p-4 rounded-lg bg-green-50 border border-green-100">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-green-500 to-green-400 flex items-center justify-center flex-shrink-0">
                                  <Timer className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <h5 className="font-semibold text-sm mb-1">{t('features.onlinePayments.title')}</h5>
                                  <p className="text-xs text-muted-foreground">{t('features.onlinePayments.description')}</p>
                                </div>
                              </div>
                            </div>

                            {/* Call to action */}
                            <div className="text-center space-y-4 pt-4 border-t">
                              <Button 
                                className="w-auto bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                                onClick={() => navigate('/subscription')}
                              >
                                {t('profile.subscribeNow')}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>


                    {user.is_subscribed && (
                      <div className="flex space-x-2 justify-end">
                        {isLoading ? (
                          <div className='animate-spin'><Loader2 /></div>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={openAddCouponDialog}>
                            <><Tag /> {t('subscription.addCoupon')}</>
                          </Button>
                        )}
                      </div>
                    )}


                    <div>
                      {paymentMethods.length > 0 && (
                        <h3 className="text-lg font-medium mb-2">{t('profile.paymentMethods')}</h3>
                      )}
                      <div className="space-y-2">
                        {paymentMethods.map((method, i) => (
                          <div key={i} className="border rounded-lg p-4 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              {isLoading && ( <><div className='animate-spin'><Loader2></Loader2></div></>)}
                              {!isLoading && (<CreditCard className="h-6 w-6 text-primary" />)}
                              <div>
                                <p className="font-medium">•••• •••• •••• {method.card.last4}</p>
                                <p className="text-xs text-muted-foreground">{t('profile.expires')} {method.card.exp_month}/{method.card.exp_year}</p>
                              </div>
                            </div>
                          <div className="space-x-2">
                          {
                              isLoading ? (
                              <div className='animate-spin'><Loader2></Loader2></div>
                              ) : (
                                <Button variant="ghost" size="sm" onClick={openChangeCardDialog}>
                                  {isMobile ? <Pencil /> : t('profile.edit')}
                                </Button>
                              )
                            }
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      {invoices.length > 0 && (
                        <>
                          <h3 className="text-lg font-medium mb-4">{t('profile.billingHistory')}</h3>
                          
                          {/* Badges des années */}
                          <div className="flex flex-wrap gap-2 mb-4">
                            {availableYears.map(year => (
                              <button
                                key={year}
                                onClick={() => setSelectedYear(year)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  selectedYear === year
                                    ? 'bg-primary text-primary-foreground shadow-md'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                                }`}
                              >
                                {year}
                              </button>
                            ))}
                          </div>
                        </>
                      )}

                      {/* Liste des factures */}
                      <div className={`rounded-lg ${filteredInvoices.length > 0 ? 'border' : ''} overflow-hidden`}>
                        {filteredInvoices.length > 0 ? (
                          filteredInvoices.map((invoice, i) => (
                            <div 
                              key={i} 
                              className={`p-4 flex justify-between items-center transition-colors duration-150 hover:bg-blue-50 ${
                                i % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                              }`}
                            >
                              <div>
                                <p className="text-gray-900">
                                  {new Intl.DateTimeFormat(locale, {
                                    weekday: isMobile ? 'short' : 'long',
                                    year: 'numeric',
                                    month: isMobile ? 'short' : 'long',
                                    day: 'numeric',
                                  }).format(invoice.created*1000)}
                                </p>
                                <p className={`text-sm ${isMobile ? 'text-xs' : 'text-muted-foreground'}`}>
                                  {invoice.lines.data[0]?.plan.nickname || t('profile.premiumPlan')}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-medium text-gray-900">
                                  {invoice && invoice?.currency?.toLowerCase() === 'eur'
                                    ? `${(invoice.amount_due / 100).toFixed(2)} EUR`
                                    : `${invoice?.currency?.toUpperCase()} ${(invoice.amount_due / 100).toFixed(2)}`
                                  }
                                </p>
                                <div className="flex justify-end">
                                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                    invoice.status === 'paid' 
                                      ? 'bg-green-100 text-green-700'
                                      : invoice.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {invoice.status === 'paid' ? t('invoices.status.paid') : 
                                    invoice.status === 'pending' ?  t('invoices.status.pending') : 
                                    invoice.status}
                                  </span>

                                  {invoice?.discount && invoice?.discount?.coupon && (
                                    <Badge className="text-xs ml-1 pl-2">{t('profile.discount')} -{invoice?.discount.coupon.percent_off}%</Badge>
                                  )}

                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          invoices.length > 0 && (
                            <div className="p-8 text-center text-gray-500">
                              <p>Aucune facture pour l'année {selectedYear}</p>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                    
                  </div>
                </CardContent>
              </Card>
            )}
            
            {activeTab === 'help' && showHelpTab && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-primary" />
                    {t('profile.Tabs.help') || 'Aide'}
                  </CardTitle>
                  <CardDescription>
                    {t('profile.help.subtitle') || 'Reprenez la visite guidée pour finir de configurer votre compte.'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-lg border p-4 space-y-3">
                    <h4 className="font-medium">{t('profile.help.checklistTitle') || 'Checklist d\'onboarding'}</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        {user.hasCreatedCategory
                          ? <CheckCircle className="h-4 w-4 text-green-500" />
                          : <X className="h-4 w-4 text-muted-foreground" />}
                        {t('profile.help.createCategory') || 'Créer une catégorie'}
                      </li>
                      {user.account_type === 'S' && (
                        <li className="flex items-center gap-2">
                          {user.hasCreatedTeacher
                            ? <CheckCircle className="h-4 w-4 text-green-500" />
                            : <X className="h-4 w-4 text-muted-foreground" />}
                          {t('profile.help.createTeacher') || 'Ajouter votre premier professeur'}
                        </li>
                      )}
                      <li className="flex items-center gap-2">
                        {user.hasCreatedStudent
                          ? <CheckCircle className="h-4 w-4 text-green-500" />
                          : <X className="h-4 w-4 text-muted-foreground" />}
                        {t('profile.help.createStudent') || 'Ajouter un élève'}
                      </li>
                      <li className="flex items-center gap-2">
                        {user.hasCreatedEvent
                          ? <CheckCircle className="h-4 w-4 text-green-500" />
                          : <X className="h-4 w-4 text-muted-foreground" />}
                        {t('profile.help.createEvent') || 'Créer votre premier cours'}
                      </li>
                    </ul>
                  </div>
                  <Button onClick={restartWizard} className="gap-2">
                    <Sparkles className="h-4 w-4" />
                    {t('profile.help.restart') || 'Relancer la visite guidée'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {activeTab === 'notifications' && (
              <Card className="border-0 rounded-none shadow-none p-1 md:border md:rounded-xl md:shadow-sm md:p-0">
               <CardHeader className="p-0 md:p-6 hidden md:block">
                  <CardTitle>{t('notifications.notificationPreferences')}</CardTitle>
                  <CardDescription>{t('notifications.notificationPreferencesSubtitle')}</CardDescription>
                </CardHeader>
                <CardContent className="p-0 md:p-6 md:pt-0">
                  <div className="space-y-6">
                    {/* Email Notifications */}
                    <div>
                      <h3 className="text-lg font-medium mb-4">{t('notifications.emailNotifications')}</h3>
                      <div className="space-y-3">
                        {Array.isArray(filteredList) && filteredList.map((item) => (
                          <div key={item.id} className="flex items-center justify-between">
                            <div>
                              <Label htmlFor={item.id} className="font-medium">{item.label}</Label>
                              <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                            <Switch
                              id={item.id}
                              checked={!!settings[item.id]}
                              onCheckedChange={() => handleToggle(item.id)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Push Notifications */}
                    <div>
                      <h3 className="text-lg font-medium mb-4">{t('notifications.pushNotifications')}</h3>
                      <div className="space-y-3">
                        {Array.isArray(filteredPushList) && filteredPushList.map((item) => (
                          <div key={item.id} className="flex items-center justify-between">
                            <div>
                              <Label htmlFor={item.id} className="font-medium">{item.label}</Label>
                              <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                            <Switch
                              id={item.id}
                              checked={!!settings[item.id]}
                              onCheckedChange={() => handleToggle(item.id)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button onClick={handleSaveNotifications} disabled={isSavingNotification}>
                        {isSavingNotification ? <Loader2 className="animate-spin" /> : t('notifications.savePreferences')}
                    </Button>
                  </div>
                </CardContent>

              </Card>
            )}
            
            {activeTab === 'sessions' && (
              <Card className="border-0 rounded-none shadow-none p-1 md:border md:rounded-xl md:shadow-sm md:p-0">
              <CardHeader className="p-0 md:p-6 hidden md:block">
                  <CardTitle>{t('sessions.sessions')}</CardTitle>
                  <CardDescription>{t('sessions.sessions_subtitle')}</CardDescription>
                </CardHeader>
               <CardContent className="p-0 md:p-6 md:pt-0">
                  <div className="space-y-4">
                  {sessionsData.map((session, i) => (
                    <div
                      key={i}
                      className={`p-4 rounded-lg ${
                        session.isCurrent ? 'bg-primary/5 border border-primary/20' : 'border'
                      }`}
                    >
                      <div className="flex justify-between">
                        <div>
                          <h4 className="font-medium flex items-center">
                            {session.device || 'Unknown device'}
                            {session.isCurrent && (
                              <span className="ml-2 text-xs bg-primary text-primary-foreground py-0.5 px-1.5 rounded-full">
                                {t('sessions.current')}
                              </span>
                            )}
                          </h4>
                          <div className="text-sm text-muted-foreground mt-1">
                            <p>{session.location ?? 'Unknown'} • {session.ip ?? '—'}</p>
                            <p className="mt-1">{t('sessions.last_active')}: {formatDateLocal(session.lastActive) ?? 'Unknown'}</p>
                          </div>
                        </div>
                        {!session.isCurrent && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleLogoutSession(session.tokenId)}
                          >
                            {t('sessions.logout')}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                    <Button variant="outline" className="text-red-500 hover:text-red-600 hover:bg-red-50 mt-2" onClick={() => handleLogoutAllSessions()}>
                    {t('sessions.logoutAll')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md bg-gradient-to-br from-green-50 to-blue-50 border-0 shadow-2xl">
          <DialogHeader className="text-center space-y-4">
            {/* Animated Success Icon */}
            <div className="mx-auto w-20 h-20 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center mb-4 animate-pulse">
              <CheckCircle className="h-12 w-12 text-white" />
            </div>
            
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-2">
              <Trophy className="h-6 w-6 text-yellow-500" />
              {t('invitations.congratulations')} !
            </DialogTitle>
            
            <DialogDescription asChild>
              <div className="text-lg text-gray-700 space-y-3">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Star className="h-5 w-5 text-yellow-400 fill-current" />
                  <span className="font-semibold">
                    {t('invitations.welcomeToSchool')} {user?.school_name ? user?.school_name : t('invitations.yourNewSchool')} !
                  </span>
                  <Star className="h-5 w-5 text-yellow-400 fill-current" />
                </div>
                
                <p>
                  {t('invitations.successfullJoined')} !
                </p>

              {/* <div className="bg-white/60 rounded-lg p-4 mt-4">
                  <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4 text-blue-500" />
                      <span>Agenda</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4 text-green-500" />
                      <span>Étudiants</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Award className="h-4 w-4 text-purple-500" />
                      <span>Confidentialité</span>
                    </div>
                  </div>
                </div>*/} 
              
              </div>
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button 
              onClick={() => {
                setShowSuccessModal(false);
                window.location.href = '/agenda';
              }}
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-6 py-3 font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
            >
              <Calendar className="h-5 w-5 mr-2" />
              {t('invitations.goToAgenda')}
            </Button>
            
            <Button 
              onClick={() => {
                setShowSuccessModal(false);
                window.location.href = '/invoices';
              }}
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-3 font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
            >
              <Files className="h-5 w-5 mr-2" />
              {t('invitations.goToInvoices')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      <AlertDialog open={isCancelPlanDialogOpen} onOpenChange={setIsCancelPlanDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('profile.cancelSubscription')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('profile.confirmCancel')}
              {t('profile.validUntil')}  <strong>{subscriptionData?.nextBillingDate || 'June 15, 2023'}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className='mt-5'>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={cancelPlanAction}
              disabled={isCancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isCancelling ? (
                <span className="flex items-center">
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  {t('common.processing')}
                </span>
              ) : (
                t('profile.yesConfirm')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isKeepCancelPlanDialogOpen} onOpenChange={setKeepIsCancelPlanDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('profile.noCancel')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('profile.noCancelDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className='mt-5'>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={keepCancelPlanAction}
              disabled={isCancelling}
              className="bg-green-500 text-destructive-foreground hover:bg-green/90"
            >
              {isCancelling ? (
                <span className="flex items-center">
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  {t('common.processing')}
                </span>
              ) : (
                t('profile.yesConfirm')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <AlertDialog open={showSchoolWarning} onOpenChange={setShowSchoolWarning}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Changing Account Type</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <div>Attention! En changeant votre compte en compte école, vous allez changer votre type d'abonnement à ThePoppyApp.</div>
              <div>Ce changement modifiera vos options disponibles et votre facturation.</div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleWarningConfirm}>J'ai compris</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isAddCouponDialogOpen} onOpenChange={setIsAddCouponDialogOpen}>
        <DialogContent hideCloseButton={true} className="p-0 max-w-md border-0 shadow-none bg-transparent">
          <PromoCodeModal onClose={() => setIsAddCouponDialogOpen(false)} token={token} reloadUser={reloadUser} />
        </DialogContent>
      </Dialog>

      <Dialog open={isChangeCardDialogOpen} onOpenChange={setIsChangeCardDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('profile.changeCard')}</DialogTitle>
            <DialogDescription>
              {t('profile.changeCardDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {isLoading ? (
              <div className="flex justify-center items-center">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : clientSecret ? (
              <Elements stripe={stripePromise} options={options}>
                <ChangeCardForm onSuccess={() => setIsChangeCardDialogOpen(false)} />
              </Elements>
            ) : (
              <p>No payment method available.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCoachCountDialog} onOpenChange={setShowCoachCountDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Combien de coaches pour votre école?</DialogTitle>
            <DialogDescription>
              Veuillez sélectionner le nombre de coaches dont vous avez besoin pour votre école.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <RadioGroup value={coachCount} onValueChange={setCoachCount} className="space-y-3">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="3" id="coaches-3" />
                <Label htmlFor="coaches-3" className="flex items-center gap-2">
                  3 coaches
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="5" id="coaches-5" />
                <Label htmlFor="coaches-5" className="flex items-center gap-2">
                  5 coaches
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="10" id="coaches-10" />
                <Label htmlFor="coaches-10" className="flex items-center gap-2">
                  10 coaches
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="custom" id="coaches-custom" />
                <Label htmlFor="coaches-custom" className="flex items-center gap-2">
                  Autre
                  {coachCount === 'custom' && customCoachCount && (
                    <Badge variant="secondary" className="ml-2">{customCoachCount}</Badge>
                  )}
                </Label>
              </div>
              
              {coachCount === 'custom' && (
                <div className="ml-6 mt-2">
                  <Input 
                    type="number" 
                    placeholder="Nombre de coaches" 
                    min="1"
                    value={customCoachCount}
                    onChange={handleCustomCoachCountChange}
                    className="w-full max-w-xs"
                  />
                </div>
              )}
            </RadioGroup>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button onClick={handleCoachCountSubmit}>Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeaveSchoolModal
        isOpen={showLeaveSchoolModal}
        onClose={() => {
          setShowLeaveSchoolModal(false);
          setSelectedSchool(null);
        }}
        onConfirm={confirmLeaveSchool}
        schoolName={selectedSchool?.school_name}
        isLoading={isLeavingSchool}
      />
      
      <AvatarModal
        isOpen={isAvatarModalOpen}
        onClose={() => setIsAvatarModalOpen(false)}
        currentAvatarUrl={profile.avatarUrl}
        onAvatarUpdate={handleAvatarUpdate}
      />
      
      <Footer />
    </div>
  );
};

export default MyAccount;
